import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { RoleService } from '../roles/roles.service';

export interface PermissionState {
  /** Whether the permission set has finished resolving from the backend. */
  loaded: boolean;
  /** True for root users or roles holding the global wildcard ('*'). */
  allowAll: boolean;
  /** Effective permission strings (catalog format: `module[.subresource].method`). */
  permissions: Set<string>;
  /** Role ids extracted from the access/id token (cognito:groups). */
  roles: string[];
}

const INITIAL_STATE: PermissionState = {
  loaded: false,
  allowAll: false,
  permissions: new Set<string>(),
  roles: []
};

interface PermissionsCache {
  v: number;
  userId: string;
  roles: string[];
  allowAll: boolean;
  permissions: string[];
  ts: number;
}

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly stateSubject = new BehaviorSubject<PermissionState>(INITIAL_STATE);
  readonly state$ = this.stateSubject.asObservable();
  private loadingStarted = false;
  // Caché persistente para evitar repetir las N llamadas a /roles/:id/permissions
  // en cada recarga. Se invalida por usuario, por cambio de roles y por TTL.
  private readonly cacheKey = 'signly.perms.cache';
  private readonly cacheVersion = 1;
  private readonly cacheTtlMs = 30 * 60 * 1000; // 30 minutos

  constructor(private authService: AuthService, private roleService: RoleService) {}

  /** Loads the effective permissions once per session. Safe to call repeatedly. */
  ensureLoaded(): void {
    if (this.loadingStarted) {
      return;
    }
    this.loadingStarted = true;
    // Si hay caché válida (mismo usuario/roles y dentro del TTL), no se llama al API.
    if (this.hydrateFromCache()) {
      return;
    }
    this.refresh();
  }

  refresh(): void {
    const session = this.authService.getSession();
    const userId = session?.user?.userId ?? '';
    const isRoot = !!session?.user?.isRoot;
    const roles = this.extractRoles();

    if (isRoot) {
      this.stateSubject.next({ loaded: true, allowAll: true, permissions: new Set(), roles });
      return;
    }

    if (!roles.length) {
      // No role groups present in the token: we cannot determine the permission
      // set, so we fail open (the backend still enforces authorization).
      this.stateSubject.next({ loaded: true, allowAll: true, permissions: new Set(), roles });
      return;
    }

    forkJoin(
      roles.map(roleId =>
        this.roleService.getRolePermissions(roleId).pipe(
          map(res => res.permissions ?? []),
          catchError(() => of<string[]>([]))
        )
      )
    ).subscribe(results => {
      const permissions = new Set<string>(results.flat());
      const allowAll = permissions.has('*');
      this.stateSubject.next({ loaded: true, allowAll, permissions, roles });
      this.persistCache(userId, roles, allowAll, permissions);
    });
  }

  /** Clears the cached permissions, e.g. on logout. */
  reset(): void {
    this.loadingStarted = false;
    this.clearCache();
    this.stateSubject.next(INITIAL_STATE);
  }

  /** Hidrata el estado desde la caché si es válida. Devuelve true si evitó llamar al API. */
  private hydrateFromCache(): boolean {
    const session = this.authService.getSession();
    const userId = session?.user?.userId ?? '';
    const isRoot = !!session?.user?.isRoot;
    const roles = this.extractRoles();

    // Root o sin roles: no requiere llamadas al API de todas formas.
    if (isRoot || !roles.length) {
      this.stateSubject.next({ loaded: true, allowAll: true, permissions: new Set(), roles });
      return true;
    }

    const cached = this.readCache();
    if (
      !cached ||
      cached.v !== this.cacheVersion ||
      cached.userId !== userId ||
      !this.sameRoles(cached.roles, roles) ||
      Date.now() - cached.ts > this.cacheTtlMs
    ) {
      return false;
    }

    const permissions = new Set<string>(cached.permissions ?? []);
    this.stateSubject.next({
      loaded: true,
      allowAll: !!cached.allowAll || permissions.has('*'),
      permissions,
      roles
    });
    return true;
  }

  private readCache(): PermissionsCache | null {
    try {
      const raw = localStorage.getItem(this.cacheKey);
      return raw ? (JSON.parse(raw) as PermissionsCache) : null;
    } catch {
      return null;
    }
  }

  private persistCache(userId: string, roles: string[], allowAll: boolean, permissions: Set<string>): void {
    try {
      const payload: PermissionsCache = {
        v: this.cacheVersion,
        userId,
        roles,
        allowAll,
        permissions: [...permissions],
        ts: Date.now()
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(payload));
    } catch {
      // private mode / quota: la app funciona igual, solo sin caché
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(this.cacheKey);
    } catch {
      // ignore
    }
  }

  private sameRoles(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  }

  snapshot(): PermissionState {
    return this.stateSubject.value;
  }

  /** Whether a module should be visible. Fails open while still loading. */
  canAccessModule(moduleKey: string): boolean {
    const state = this.stateSubject.value;
    if (!state.loaded || state.allowAll) {
      return true;
    }
    const prefix = `${moduleKey}.`;
    for (const permission of state.permissions) {
      if (permission === '*' || permission === `${moduleKey}.*` || permission.startsWith(prefix)) {
        return true;
      }
    }
    return false;
  }

  observeModule(moduleKey: string): Observable<boolean> {
    return this.state$.pipe(map(() => this.canAccessModule(moduleKey)));
  }

  /** Checks an exact permission (e.g. 'users.post'). Fails open while loading. */
  has(permission: string): boolean {
    const state = this.stateSubject.value;
    if (!state.loaded || state.allowAll || state.permissions.has('*')) {
      return true;
    }
    if (state.permissions.has(permission)) {
      return true;
    }
    const module = permission.split('.')[0];
    return state.permissions.has(`${module}.*`);
  }

  private extractRoles(): string[] {
    const session = this.authService.getSession();
    const fromId = this.decodeGroups(session?.idToken);
    if (fromId.length) {
      return fromId;
    }
    return this.decodeGroups(session?.accessToken);
  }

  private decodeGroups(token: string | undefined | null): string[] {
    if (!token) {
      return [];
    }
    try {
      const payloadSegment = token.split('.')[1];
      if (!payloadSegment) {
        return [];
      }
      const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded));
      const groups = payload['cognito:groups'];
      return Array.isArray(groups) ? groups.map(String) : [];
    } catch {
      return [];
    }
  }
}
